/**
 * UserContext - Contexto de usuario con perfil y preferencias
 * 
 * Este contexto consume el AuthProvider para obtener el usuario autenticado
 * y provee funcionalidades adicionales como:
 * - updateUser, saveChanges, cancelChanges
 * - upgradePlan, requireAuth
 * - Manejo de preferencias (idioma, tema, etc.)
 * 
 * YA NO tiene su propio listener de onAuthStateChange.
 */
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import i18n, { resources as i18nResources } from '../i18n';
import { LANGUAGES_40 } from '../utils/i18nOptions';
import { saveProfile, saveSettings, updateEmail, captureReferralFromURL, applyReferralBonusIfEligible } from '../features/profile/api';
import { logEvent } from '../telemetry';
import { useAuth } from '../auth/AuthProvider';

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser debe ser usado dentro de UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  // Obtener usuario desde AuthProvider
  const auth = useAuth();
  
  // Estado local para cambios pendientes (antes de guardar)
  const [localOverrides, setLocalOverrides] = useState({});
  const originalRef = useRef(null);
  const lastEmailUpdateRef = useRef({ email: null, ts: 0 });

  const supportedLangs = useRef(new Set((LANGUAGES_40 || []).map(l => (l.code || '').toLowerCase())));

  const detectBrowserLanguage = () => {
    try {
      const langs = (navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language || 'en']).map(l => String(l).toLowerCase());
      for (const l of langs) {
        const base = l.split('-')[0];
        if (supportedLangs.current.has(l)) return l;
        if (supportedLangs.current.has(base)) return base;
      }
    } catch {}
    return 'en';
  };

  // Usuario combinado: datos de AuthProvider + overrides locales
  // ✅ FIX #4: auth.loading incluido en deps para evitar memo stale
  const user = React.useMemo(() => {
    // Si hay sesión pero perfil no cargado aún → user mínimo (NUNCA null con sesión)
    if (!auth.user && auth.session) {
      return {
        id: auth.session.user?.id,
        email: auth.session.user?.email || '',
        name: auth.session.user?.user_metadata?.name || '',
        status: auth.profileLoading ? 'profile_loading' : 'session_only',
        plan: (() => {
          try {
            const userId = auth.session.user?.id;
            const cached = userId ? localStorage.getItem(`iv1_last_plan:${userId}`) : null;
            if (cached && ['trial', 'pro', 'pro_plus'].includes(cached)) return cached;
          } catch {}
          return 'trial';
        })(),
        trialDaysLeft: (() => {
          try {
            const userId = auth.session.user?.id;
            const cachedEnds = userId ? localStorage.getItem(`iv1_trial_ends:${userId}`) : null;
            if (cachedEnds) {
              const diff = Math.ceil((new Date(cachedEnds).getTime() - Date.now()) / 86400000);
              return Number.isFinite(diff) ? diff : 7;
            }
          } catch {}
          return 7;
        })(),
        preferences: {
          language: null,
          theme: 'dark',
          email_notifications: true,
          push_notifications: false,
          marketing_optin: true,
          safe_mode: true,
          timezone: 'America/Bogota'
        }
      };
    }

    // Sin sesión y sin user
    if (!auth.user && !auth.session) {
      if (auth.loading) {
        return {
          status: 'loading',
          plan: 'trial',
          preferences: {
            language: null,
            theme: 'dark',
            email_notifications: true,
            push_notifications: false,
            marketing_optin: true,
            safe_mode: true,
            timezone: 'America/Bogota'
          }
        };
      }
      return null;
    }

    const authUser = auth.user || {
      id: undefined,
      email: '',
      name: '',
      emailVerified: false,
      referralCode: '',
      referrals: { sent: 0, successful: 0, bonusDays: 0 },
      achievements: [],
      preferences: {
        language: null,
        theme: 'dark',
        email_notifications: true,
        push_notifications: false,
        marketing_optin: true,
        safe_mode: true,
        timezone: 'America/Bogota'
      }
    };
    
    return {
      ...authUser,
      ...localOverrides,
      preferences: {
        ...authUser.preferences,
        ...(localOverrides.preferences || {})
      }
    };
  }, [auth.user, auth.loading, auth.profileLoading, auth.session, localOverrides]);

  // Guardar referencia original cuando AuthProvider entrega usuario
  useEffect(() => {
    if (auth.user?.id && !originalRef.current) {
      originalRef.current = auth.user;
    }
    // Limpiar overrides cuando hay nuevo usuario desde AuthProvider
    if (auth.user?.id) {
      setLocalOverrides({});
    }
  }, [auth.user]);

  // Capturar referral de URL al montar
  useEffect(() => {
    captureReferralFromURL();
  }, []);

  // Aplicar bono de referido si aplica — dep auth.user?.id es intencional (solo disparar al cambiar id)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (auth.user?.id) {
      applyReferralBonusIfEligible(auth.user).catch(() => {});
    }
  }, [auth.user?.id]);

  // Si no hay preferencia aún, establecer idioma inicial priorizando ?lang, localStorage, i18n actual, navegador (fallback 'en')
  useEffect(() => {
    if (!originalRef.current && !auth.user?.id) {
      try {
        const params = new URLSearchParams(window.location.search || '');
        const urlLangRaw = params.get('lang');
        const normalize = (lng) => String(lng || '').toLowerCase().split('-')[0];
        const urlLang = normalize(urlLangRaw);
        const savedLang = normalize(localStorage.getItem('i18n.lang'));
        const current = normalize(i18n?.language || '');
        const browser = normalize(detectBrowserLanguage());
        const candidates = [urlLang, savedLang, current, browser, 'en'].filter(Boolean);
        let chosen = candidates.find(l => supportedLangs.current.has(l));
        if (!chosen) chosen = 'en';

        setLocalOverrides(prev => ({
          ...prev,
          preferences: { ...prev.preferences, language: chosen }
        }));

        try { if (urlLang) localStorage.setItem('i18n.lang', chosen); } catch {}
        try { if (i18n?.isInitialized && i18n.language !== chosen) { i18n.changeLanguage(chosen).catch(()=>{}); } } catch {}
        try { if (typeof document !== 'undefined') document.documentElement.setAttribute('lang', chosen); } catch {}
      } catch {}
    }
  }, [auth.user?.id]);

  // Reflejar idioma en <html lang="...">
  // FIX(p8-enit): priorizar ?lang= URL param y localStorage sobre el idioma del perfil de Supabase.
  // Sin este fix, cuando el usuario navega con ?lang=en, el perfil cargado con language='es' sobrescribía
  // el idioma elegido explícitamente, causando mezcla de español en EN e IT (H-001, H-002).
  useEffect(() => {
    try {
      const profileLang = (user?.preferences?.language || 'en').toLowerCase();
      // Preferencia explícita: ?lang= URL param tiene prioridad máxima
      const urlParams = new URLSearchParams(window.location.search || '');
      const urlLangRaw = (urlParams.get('lang') || '').toLowerCase().split('-')[0];
      // Segunda prioridad: lo que el usuario eligió y quedó en localStorage (guardado por index.js y por este efecto)
      const storedLang = (() => { try { return (localStorage.getItem('i18n.lang') || '').toLowerCase().split('-')[0]; } catch { return ''; } })();
      // Elegir: URL param > localStorage (si difiere del perfil y es un idioma soportado) > perfil Supabase
      const supported = Object.keys(i18nResources || {});
      const isSupported = (l) => l && supported.length > 0 ? supported.includes(l) : !!l;
      let lang = profileLang;
      if (urlLangRaw && isSupported(urlLangRaw)) {
        lang = urlLangRaw;
      } else if (storedLang && storedLang !== profileLang && isSupported(storedLang)) {
        lang = storedLang;
      }
      if (typeof document !== 'undefined') document.documentElement.setAttribute('lang', lang);
      if (i18n?.isInitialized && i18n.language !== lang) {
        i18n.changeLanguage(lang).catch(() => {});
      }
      try { localStorage.setItem('i18n.lang', lang); } catch {}
    } catch {}
  }, [user?.preferences?.language]);

  // Sincronizar cuando i18n cambie de idioma (guardar y reflejar en estado)
  useEffect(() => {
    const onLang = (lng) => {
      const lang = (lng || 'en').toLowerCase();
      setLocalOverrides(prev => ({ ...prev, preferences: { ...prev.preferences, language: lang } }));
      try { localStorage.setItem('i18n.lang', lang); } catch {}
      try { if (typeof document !== 'undefined') document.documentElement.setAttribute('lang', lang); } catch {}
    };
    try { i18n.on('languageChanged', onLang); } catch {}
    return () => { try { i18n.off('languageChanged', onLang); } catch {} };
  }, []);


  const updateUser = (updates) => {
    // Permite actualizar campos planos o anidados en preferences
    setLocalOverrides(prev => {
      const next = { ...prev };
      Object.entries(updates || {}).forEach(([k, v]) => {
        if (k.startsWith('preferences.')) {
          const key = k.split('.')[1];
          next.preferences = { ...(prev.preferences || {}), [key]: v };
        } else {
          next[k] = v;
        }
      });
      return next;
    });
  };

  // Guardar cambios del perfil y preferencias
  const saveChanges = async (overrides) => {
    // Verificar que hay sesión activa
    if (!auth.isAuthenticated) {
      throw new Error('No hay sesión activa de Supabase. Por favor inicia sesión.');
    }
    
    const payload = {
      ...user,
      ...(overrides || {}),
      preferences: { ...user.preferences, ...(overrides?.preferences || {}) }
    };
    // Persistir perfil
    await saveProfile({
      name: payload.name,
      language: payload?.preferences?.language || payload.language,
      timezone: payload?.preferences?.timezone || payload.timezone,
    });
    // Persistir preferencias
    await saveSettings({
      email_notifications: payload?.preferences?.email_notifications,
      push_notifications: payload?.preferences?.push_notifications,
      marketing_optin: payload?.preferences?.marketing_optin,
      safe_mode: payload?.preferences?.safe_mode,
      consent: true,
    });
    // Si el email cambió, iniciar flujo de verificación
    if (originalRef.current && payload.email && payload.email !== originalRef.current.email) {
      const now = Date.now();
      const last = lastEmailUpdateRef.current;
      const withinWindow = last.email === payload.email && (now - last.ts) < 70000; // 70s
      if (!withinWindow) {
        try {
          await updateEmail(payload.email);
          lastEmailUpdateRef.current = { email: payload.email, ts: now };
        } catch (e) {
          const msg = String(e?.message || '').toLowerCase();
          if (msg.includes('for security purposes') || msg.includes('request this after') || e?.status === 429) {
            // Silenciar reintentos demasiado rápidos
            lastEmailUpdateRef.current = { email: payload.email, ts: now };
          } else {
            throw e;
          }
        }
      }
    }
    // Recargar perfil desde AuthProvider
    const updated = await auth.refreshProfile();
    if (updated) {
      originalRef.current = updated;
      setLocalOverrides({});
    }
    return true;
  };

  const cancelChanges = () => {
    setLocalOverrides({});
  };

  const upgradePlan = (newPlan) => {
    setLocalOverrides(prev => ({ 
      ...prev, 
      plan: newPlan === 'free' ? 'trial' : newPlan,
      trialDaysLeft: newPlan === 'trial' ? (user.trialDaysLeft || 7) : 0
    }));
    try {
      if (newPlan === 'pro' || newPlan === 'pro_plus') {
        logEvent('payment', { userId: user?.id, email: user?.email, plan: newPlan, amount: newPlan==='pro_plus'? 9.79 : 5.79, currency:'USD' });
      } else if (newPlan === 'trial' || newPlan === 'free') {
        logEvent('churn', { userId: user?.id, email: user?.email, from: user?.plan });
      }
    } catch {}
  };

  const addAchievement = (achievement) => {
    setLocalOverrides(prev => ({
      ...prev,
      achievements: [...(user.achievements || []), achievement]
    }));
  };

  const incrementReferrals = () => {
    setLocalOverrides(prev => ({
      ...prev,
      referrals: {
        ...(user.referrals || {}),
        sent: ((user.referrals?.sent) || 0) + 1
      }
    }));
  };

  const value = {
    user,
    updateUser,
    saveChanges,
    cancelChanges,
    upgradePlan,
    addAchievement,
    incrementReferrals,
    // requireAuth ahora usa el método del AuthProvider
    requireAuth: auth.requireAuth,
    isTrialExpired: () => user?.plan === 'trial' && (user?.trialDaysLeft || 0) <= 0,
    // loading = solo carga de sesión (rápido). profileLoading = carga de perfil (puede tardar)
    loading: auth.loading,
    profileLoading: auth.profileLoading,
    authLoading: auth.loading, // Alias: true SOLO mientras getSession() resuelve
    isAuthenticated: auth.isAuthenticated,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};